import { computed, EventEmitter, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  JourneyEndpointMap,
  JourneyKey,
  JourneyRenderMode,
  JourneyStageData,
  JourneyStepResponse,
  JourneyCompleteResponse,
  CallbackFieldMeta,
} from '../interfaces/journey.interface';
import { Callback } from '../interfaces/response.interface';
import { mapCallbacksForNode } from '../utils/journey-callback-mapper';
import { extractTransportErrorMessage } from '../journey-engine/errors/journey-error-extractor';

/** Raw Ping response can be either a step (has callbacks) or terminal (has tokenId) */
type PingResponse = JourneyStepResponse | JourneyCompleteResponse;

/** Extensible map — add new journeys here */
const JOURNEY_ENDPOINTS: JourneyEndpointMap = {
  UPDATE_PROFILE: '/profile/update-profile',
  UPDATE_PROFILE_DETAILS: '/profile/update-firstname-lastname',
  CHANGE_PASSWORD: '/profile/update-password',
  MFA_REGISTRATION: '/profile/add-MFA',
  PASSKEY_REGISTRATION: '/profile/add-passkey',
};

@Injectable({ providedIn: 'root' })
export class JourneyService {
  private http = inject(HttpClient);

  // ── Signals ──────────────────────────────────────────
  private _currentStep = signal<JourneyStepResponse | null>(null);
  private _isActive = signal(false);
  private _isLoading = signal(false);
  private _isComplete = signal(false);
  private _error = signal<string | null>(null);
  private _journeyKey = signal<JourneyKey | null>(null);
  private prefillValues: Record<string, any> | null = null;
  private prefillApplied = false;

  /** Read-only public signals */
  readonly currentStep = this._currentStep.asReadonly();
  readonly isActive = this._isActive.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isComplete = this._isComplete.asReadonly();
  readonly error = this._error.asReadonly();
  readonly journeyKey = this._journeyKey.asReadonly();

  /** Parsed stage data from current step */
  readonly stageData = computed<JourneyStageData | null>(() => {
    const step = this._currentStep();
    if (!step?.stage) return null;
    try {
      return JSON.parse(step.stage);
    } catch {
      return null;
    }
  });

  /** Shortcut computeds */
  readonly renderer = computed<JourneyRenderMode>(() => {
    const step = this._currentStep();
    if (step && this.isWebAuthnAuthenticationStep(step)) {
      return 'inapp';
    }

    const value = this.stageData()?.render ?? this.stageData()?.renderer ?? 'dialog';
    return (value === 'inapp' || value === 'confirmation' || value === 'dialog')
      ? value
      : 'dialog';
  });
  readonly nodeId = computed(() => this.stageData()?.nodeId ?? null);
  readonly journeyId = computed(() => this.stageData()?.journeyId ?? null);

  /** Mapped callback field metadata for the current step */
  readonly fieldMetas = computed<CallbackFieldMeta[]>(() => {
    const cbs = this._currentStep()?.callbacks;
    return cbs ? mapCallbacksForNode(cbs, this.nodeId()) : [];
  });

  /** Fires when a journey completes */
  readonly onComplete = new EventEmitter<any>();

  /** Fires when the journey encounters an error */
  readonly onError = new EventEmitter<string>();

  // ── Public API ───────────────────────────────────────

  /**
   * Start a new journey.
   * @param journeyKey  Key into JOURNEY_ENDPOINTS
   * @param payload     Initial payload (e.g. { userName })
   */
  startJourney(journeyKey: JourneyKey, payload: any = {}): void {
    const endpoint = JOURNEY_ENDPOINTS[journeyKey];
    if (!endpoint) {
      this._error.set(`Unknown journey: ${journeyKey}`);
      this.onError.emit(`Unknown journey: ${journeyKey}`);
      return;
    }

    this.resetState();
    this._journeyKey.set(journeyKey);
    this.prefillValues = this.extractPrefillValues(payload);
    this.prefillApplied = false;
    this._isActive.set(true);
    this._isLoading.set(true);

    this.http
      .post<PingResponse>(`${environment.apiURL}${endpoint}`, payload)
      .subscribe({
        next: (res) => this.handleResponse(res),
        error: (err) => this.handleError(err),
      });
  }

  /**
   * Submit the current step's filled callbacks to Ping.
   * Caller should have filled callback input values before calling this.
   */
  submitStep(): void {
    const step = this._currentStep();
    if (!step) return;

    const journeyKey = this._journeyKey();
    const endpoint = journeyKey ? JOURNEY_ENDPOINTS[journeyKey] : null;
    if (!endpoint) return;

    this._isLoading.set(true);
    this._error.set(null);

    // Send raw Ping shape back: { authId, callbacks, stage }
    const body: JourneyStepResponse = {
      authId: step.authId,
      callbacks: step.callbacks,
      stage: step.stage,
    };

    this.http
      .post<PingResponse>(`${environment.apiURL}${endpoint}`, body)
      .subscribe({
        next: (res) => this.handleResponse(res),
        error: (err) => this.handleError(err),
      });
  }

  /**
   * Cancel the active journey and reset all state.
   */
  cancelJourney(): void {
    this.resetState();
  }

  /**
   * Fill a specific callback's input value by callback index.
   */
  fillCallbackInput(callbackIndex: number, inputIndex: number, value: any): void {
    const step = this._currentStep();
    if (!step?.callbacks?.[callbackIndex]?.input?.[inputIndex]) return;
    step.callbacks[callbackIndex].input[inputIndex].value = value;
  }

  /**
   * Find a callback in the current step by its type string.
   */
  getCallbackByType(type: string): Callback | undefined {
    return this._currentStep()?.callbacks?.find(cb => cb.type === type);
  }

  // ── Internal ─────────────────────────────────────────

  private handleResponse(res: PingResponse): void {
    this._isLoading.set(false);

    // Journey complete: terminal responses can include tokenId, or just successUrl/realm.
    if (this.isJourneyCompleteResponse(res)) {
      this._isComplete.set(true);
      this._isActive.set(false);
      this._currentStep.set(null);
      this.onComplete.emit(res as JourneyCompleteResponse);
      return;
    }

    // Next step: raw Ping returns { authId, callbacks, stage }
    const nextStep = res as JourneyStepResponse;
    this.clearOtpInputsForOtpCollector(nextStep);
    this.applyPrefillIfNeeded(nextStep);
    this._currentStep.set(nextStep);
  }

  private isJourneyCompleteResponse(res: PingResponse): res is JourneyCompleteResponse {
    const complete = res as JourneyCompleteResponse;
    return Boolean(complete?.tokenId)
      || (typeof complete?.successUrl === 'string' && typeof complete?.realm === 'string');
  }

  private handleError(err: any): void {
    this._isLoading.set(false);
    const msg = extractTransportErrorMessage(err);
    this._error.set(msg);
    this.onError.emit(msg);
  }

  private resetState(): void {
    this._currentStep.set(null);
    this._isActive.set(false);
    this._isLoading.set(false);
    this._isComplete.set(false);
    this._error.set(null);
    this._journeyKey.set(null);
    this.prefillValues = null;
    this.prefillApplied = false;
  }

  private extractPrefillValues(payload: any): Record<string, any> | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const values: Record<string, any> = {};
    if (payload['givenName'] !== undefined) values['givenName'] = payload['givenName'];
    if (payload['firstName'] !== undefined) values['firstName'] = payload['firstName'];
    if (payload['sn'] !== undefined) values['sn'] = payload['sn'];
    if (payload['lastName'] !== undefined) values['lastName'] = payload['lastName'];

    return Object.keys(values).length ? values : null;
  }

  private applyPrefillIfNeeded(step: JourneyStepResponse): void {
    if (this.prefillApplied || !this.prefillValues) {
      return;
    }

    step.callbacks.forEach((callback) => {
      if (callback.type !== 'StringAttributeInputCallback' && callback.type !== 'NameCallback') {
        return;
      }

      const fieldName = callback.output?.find((o) => o.name === 'name')?.value;
      let value: any;

      if (fieldName === 'givenName') {
        value = this.prefillValues?.['givenName'] ?? this.prefillValues?.['firstName'];
      } else if (fieldName === 'sn') {
        value = this.prefillValues?.['sn'] ?? this.prefillValues?.['lastName'];
      }

      if (value !== undefined && value !== null) {
        const textInput = callback.input?.find((i) => i.name?.toLowerCase().includes('valid'))
          ? callback.input?.find((i) => !i.name?.toLowerCase().includes('validateonly'))
          : callback.input?.[0];

        if (textInput) {
          textInput.value = value;
        }

        const outputValue = callback.output?.find((o) => o.name === 'value');
        if (outputValue) {
          outputValue.value = value;
        }
      }
    });

    this.prefillApplied = true;
  }

  private clearOtpInputsForOtpCollector(step: JourneyStepResponse): void {
    const nodeId = this.readNodeId(step.stage);
    if (nodeId !== 'otpcollector') {
      return;
    }

    step.callbacks.forEach((callback) => {
      if (callback.type === 'PasswordCallback' && callback.input?.[0]) {
        callback.input[0].value = '';
      }
    });
  }

  private readNodeId(stage: string | undefined): string {
    if (!stage) {
      return '';
    }

    try {
      const parsed = JSON.parse(stage) as JourneyStageData;
      return String(parsed?.nodeId ?? '').toLowerCase();
    } catch {
      return '';
    }
  }

  private isWebAuthnAuthenticationStep(step: JourneyStepResponse): boolean {
    const metadata = step.callbacks?.find((cb) => cb.type === 'MetadataCallback');
    const data = metadata?.output?.find((o) => o.name === 'data')?.value as Record<string, any> | undefined;
    return data?.['_action'] === 'webauthn_authentication';
  }
}
